import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

import { Col } from 'react-bootstrap'

import { CONFIG } from 'Config/config'
import { DATE_NAME_FORMAT } from 'constants.js'
import { dateFormatter } from 'Utils/Formatters'
import { getMetricResultsFromConversionsMap } from '../../../../Utils/Mission'
import { ModalLightbox } from '../../../../Components/Modal/ModalLightbox'
import { LinkExternal } from '../../../../Components/LinkExternal'
import { MissionMap } from '../../../../Components/Missions/MissionMap'
import {
  Section,
  SectionLabel,
  SectionRow,
} from '../../../../Components/Section'
import {
  dropTimeZoneFromDate,
  formatWeekNumber,
  getWeekNumber,
  simplerGetTimeOfDayFromDate,
} from '../../../../shared/libraries/utils/date'
import { authorization } from '../../../../Module/Auth/Auth'
import { formatFieldValue } from '../../../../Utils/Formatters'
import {
  CAMPAIGN_ENUM_TYPES,
  MISSION_ENUM_TYPES,
} from '../../../../shared/constants'
import { getDistance } from '../../../../shared/libraries/geo'
import format from 'date-fns/format'
import NoShowBadge from '../../../../Components/Missions/NoShowBadge'
import { Box } from '@material-ui/core'
import moment from 'moment'
import { useNoShowLog } from '../../hooks/useNoShowLog'
import { addCurrencySign } from '../../../../Components/CampaignQuote/util'
import { BonusDisqualifiedBadge } from '../../../../Components/Missions/BonusDisqualifiedBadge/BonusDisqualifiedBadge'
import { PATHS } from '../../../../Config/Routing/paths'
import LinkListItem from '../../../../Components/LinkListItem'
import { getLocationProfileLink } from '../../../../Utils/Link'

const getStepsFromActivity = activities => {
  const stepLength = 0.72
  const meters = activities.reduce((acc, activity, index, activities) => {
    if (activities[index + 1]) {
      acc += getDistance(
        activity.latitude,
        activity.longitude,
        activities[index + 1].latitude,
        activities[index + 1].longitude,
      )
    }
    return acc
  }, 0)
  return Math.floor(meters / stepLength)
}

const MissionDetails = ({ mission, campaign }) => {
  const metrics = useMemo(
    () =>
      Array.from(getMetricResultsFromConversionsMap(mission.conversions)).map(
        ([metricId, { name, value }]) => ({ name, value }),
      ),
    [mission.conversions],
  )

  const {
    logs: noShowLogs,
    showBadge: showNoShowBadge,
    showHistory,
  } = useNoShowLog({
    mission,
  })

  const location = mission.location
    ? mission.location
    : mission.boundary.length
    ? {
        latitude: mission.boundary[0].latitude,
        longitude: mission.boundary[0].longitude,
        name: mission.label,
      }
    : null

  const timeStart = useMemo(
    () => dropTimeZoneFromDate(mission.timeRangeStart),
    [mission.timeRangeStart],
  )

  const timeOfDay = useMemo(
    () =>
      mission.cityPlan.city
        ? simplerGetTimeOfDayFromDate(timeStart, mission.cityPlan.city)
        : '',
    [timeStart, mission.cityPlan.city],
  )

  const isCityManagerAssistant = authorization.isCityManagerAssistant()
  const isSpyRole = authorization.isSpy()

  const shouldShowCurrentPosition =
    authorization.isSpy() ||
    authorization.isCityManager() ||
    authorization.isAdmin()

  const shouldLocationProfileLinkBeVisible =
    (authorization.isCityManager() || authorization.isAdmin()) &&
    !isCityManagerAssistant &&
    !isSpyRole

  const shouldShowMissionCheckoutFeedback =
    (authorization.isCityManager() || authorization.isAdmin()) &&
    campaign.type !== CAMPAIGN_ENUM_TYPES.DIRECTMAIL

  const primaryMetric = mission.campaign.metrics.nodes
    ? mission.campaign.metrics.nodes.find(m => m.isPrimary)
    : null

  const missionLogs = mission.logs
  const locationLink = getLocationProfileLink(mission.city.id, location.id)

  const isNeedToShowBonusPerMetric =
    primaryMetric &&
    campaign.bonusEnabled &&
    !isCityManagerAssistant &&
    !isSpyRole

  const isNeedToShowBonusThreshold =
    campaign.bonusEnabled && !isCityManagerAssistant && !isSpyRole

  const canSeeSensitiveData = !authorization.isCityManagerAssistant()

  return (
    <Section
      title={
        <Box display={'flex'} gap="20px">
          Mission Details
          {showNoShowBadge && <NoShowBadge />}
          {mission.bonusDisqualified &&
            authorization.isCityManager() &&
            !authorization.isCityManagerAssistant() && (
              <BonusDisqualifiedBadge />
            )}
        </Box>
      }
    >
      <SectionRow>
        <Col xs={3} cypress_id="mission-details-client-div">
          <SectionLabel>Client</SectionLabel>
          {mission.client.name}
        </Col>
        <Col xs={3} cypress_id="mission-details-date-div">
          <SectionLabel>Date</SectionLabel>
          {`${dateFormatter(timeStart, DATE_NAME_FORMAT)}, (${formatWeekNumber(
            getWeekNumber(timeStart),
          )})`}
        </Col>
        <Col xs={3} cypress_id="mission-details-time-div">
          <SectionLabel>Time</SectionLabel>
          {format(new Date(timeStart), 'hh:mma')} {timeOfDay}
        </Col>
        <Col xs={3} cypress_id="mission-details-flyers-qty-div">
          <SectionLabel>Flyers Qty</SectionLabel>
          {`${
            mission.totalFlyersDistributed ? mission.totalFlyersDistributed : 0
          } 
        / ${mission.flyerCodeV2 ? mission.flyerCodeV2.quantity : ''}`}
        </Col>
      </SectionRow>

      <SectionRow>
        <Col xs={3} cypress_id="mission-details-flyer-pack-div">
          <SectionLabel>Flyer pack</SectionLabel>
          {mission.checkInFlyerPackImage ? (
            mission.isAnonymize ? (
              <div>Photo uploaded & reviewed by operations</div>
            ) : (
              <ModalLightbox
                linkText="View flyer pack photo"
                image={mission.checkInFlyerPackImage}
              />
            )
          ) : (
            '-'
          )}
        </Col>
        {!isSpyRole ? (
          <Col xs={3} cypress_id="mission-details-flyer-code-div">
            <SectionLabel>Flyer Code</SectionLabel>
            {mission.flyerCodeV2 ? mission.flyerCodeV2.code : '-'}

            {mission.checkInFlyerCodeImage ? (
              mission.isAnonymize ? (
                <div>Photo uploaded & reviewed by operations</div>
              ) : (
                <ModalLightbox
                  linkText="View flyer code photo"
                  image={mission.checkInFlyerCodeImage}
                  cypress_id="mission-details-flyer-code-photo-link-div"
                />
              )
            ) : (
              '-'
            )}
          </Col>
        ) : null}
        {location && mission.type === MISSION_ENUM_TYPES.HANDTOHAND ? (
          <Col xs={3} cypress_id="mission-details-location-div">
            <SectionLabel>Location</SectionLabel>
            {shouldLocationProfileLinkBeVisible ? (
              <LinkExternal href={locationLink} target="_blank">
                {location.name}
              </LinkExternal>
            ) : (
              `${location.name}`
            )}
            <LinkExternal
              href={CONFIG.GOOGLE_PLACE_URL(location)}
              target="_blank"
              className="view-map"
            >
              view map
            </LinkExternal>
          </Col>
        ) : null}

        <Col xs={3}>
          <SectionLabel>Checkin Selfie</SectionLabel>
          {mission.checkInSelfie ? (
            mission.isAnonymize ? (
              <div>Photo uploaded & reviewed by operations</div>
            ) : (
              <ModalLightbox
                linkText="View check in photo"
                image={mission.checkInSelfie}
              />
            )
          ) : (
            '-'
          )}
        </Col>
      </SectionRow>

      <SectionRow>
        {showHistory && canSeeSensitiveData && !isSpyRole && (
          <Col xs={3}>
            <SectionLabel>Mission history</SectionLabel>
            <Box display={'flex'} flexDirection="column">
              {missionLogs && missionLogs.length
                ? missionLogs.map(log => (
                    <LinkListItem
                      text={log}
                      target="_blank"
                      href={`${PATHS.main.dashboard}${
                        PATHS.dashboard.campaigns
                      }/${campaign.id}${PATHS.campaign.conversions}`}
                    />
                  ))
                : null}
              {noShowLogs
                ? noShowLogs.map(log => (
                    <LinkListItem
                      text={`No-show on ${moment(log.date).format(
                        'DD/MM/YYYY',
                      )} at ${moment(log.date).format('hh:mm A')} for ${
                        log.user.firstName
                      } ${log.user.lastName}`}
                      target="_blank"
                      href={`/dashboard/cities/${mission.city.id}/ambassadors/${
                        log.user.id
                      }`}
                    />
                  ))
                : '-'}
            </Box>
          </Col>
        )}

        <Col xs={3}>
          <SectionLabel>Steps Walked</SectionLabel>
          {mission.checkoutStepsWalked ||
            getStepsFromActivity(mission.activities) ||
            '-'}
        </Col>

        <Col xs={3} cypress_id="mission-details-comment-div">
          <SectionLabel>Comment</SectionLabel>
          {mission.comments || '-'}
        </Col>
        {mission.type === MISSION_ENUM_TYPES.LETTERBOX && (
          <div>
            <Col xs={3}>
              <SectionLabel>Addresses</SectionLabel>
              {formatFieldValue(mission.householdsAmount)}
            </Col>
            <Col xs={3}>
              <SectionLabel>Density</SectionLabel>
              {formatFieldValue(mission.density)}
            </Col>
          </div>
        )}
        {canSeeSensitiveData && (
          <Col xs={3}>
            <SectionLabel>Scans</SectionLabel>
            {mission.amountScans}
          </Col>
        )}
        {canSeeSensitiveData && !isSpyRole
          ? metrics.map(({ name, value }) => (
              <Col xs={3}>
                <SectionLabel>{name}</SectionLabel>
                {value}
              </Col>
            ))
          : null}
      </SectionRow>

      {shouldShowMissionCheckoutFeedback && (
        <SectionRow>
          {mission.cvrPrediction && (
            <Col xs={3}>
              <SectionLabel>{`CVR Prediction`}</SectionLabel>
              {(mission.cvrPrediction * 100).toFixed(2) || '-'}
            </Col>
          )}

          <Col xs={3} cypress_id="mission-details-feedback-div">
            <SectionLabel>Mission feedback</SectionLabel>
            {mission.checkoutFeedback || '-'}
          </Col>
          {isNeedToShowBonusPerMetric ? (
            <Col xs={3} cypress_id="mission-details-bonus-per-conversion-div">
              <SectionLabel>{`Bonus per ${
                primaryMetric.name == 'Other'
                  ? primaryMetric.label
                  : primaryMetric.name
              }`}</SectionLabel>
              {mission.bonusPerConversion
                ? addCurrencySign(
                    (mission.bonusPerConversion / 100).toFixed(2),
                    mission.cityPlan.city.country.currencySign,
                  )
                : '-'}
            </Col>
          ) : null}
          {isNeedToShowBonusThreshold ? (
            <Col xs={3} cypress_id="mission-details-bonus-threshold-div">
              <SectionLabel>{`Bonus threshold(%)`}</SectionLabel>
              {mission.bonusThreshold ? mission.bonusThreshold : '-'}
            </Col>
          ) : null}
        </SectionRow>
      )}

      <SectionRow>
        <Col xs={12}>
          {mission.id && (
            <MissionMap
              mission={mission}
              shouldShowCurrentPosition={shouldShowCurrentPosition}
            />
          )}
        </Col>
      </SectionRow>
    </Section>
  )
}

MissionDetails.propTypes = {
  mission: PropTypes.object.isRequired,
}

export { MissionDetails }
